import type { FC, ReactNode } from 'react'

import { compareText } from '@/lib/academic'
import { formatDateTime } from '@/lib/dates'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Card, CardHeader } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/feedback'
import { Select } from '@/components/ui/select'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { useAcademic } from '@/components/providers/academic-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { History, ShieldCheck } from 'lucide-react'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

const ActivityLogsPage: FC = (): ReactNode => {
    const { data, currentUser, userById } = useAcademic()
    const { ready, get, set } = useQueryParams()

    if (!ready) return <PageSkeleton />

    if (currentUser.role !== 'admin') {
        return (
            <Card>
                <EmptyState icon={<ShieldCheck />} title='Khusus admin' description='Log aktivitas hanya dapat dibuka oleh akun dengan peran admin.' />
            </Card>
        )
    }

    const modules = Array.from(new Set(data.logs.map(log => log.module))).sort(compareText)
    const requestedModule = get('modul') ?? ''
    const moduleFilter = modules.includes(requestedModule) ? requestedModule : ''

    const rows = [...data.logs]
        .filter(log => !moduleFilter || log.module === moduleFilter)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .slice(0, 200)

    return (
        <>
            <Seo title='Log aktivitas' />

            <PageHeader
                eyebrow='Administrasi'
                title='Log Aktivitas'
                description='Riwayat aksi penting di sistem (200 entri terbaru).'
            />

            <Card>
                <CardHeader
                    icon={<History />}
                    title='Riwayat aktivitas'
                    description={`${rows.length} entri ditampilkan`}
                    action={(
                        <Select
                            aria-label='Filter modul'
                            value={moduleFilter}
                            onChange={event => set({ modul: event.target.value || null })}
                            className='h-9'
                            wrapperClassName='w-full sm:w-56'
                        >
                            <option value=''>Semua modul</option>
                            {modules.map(item => <option key={item} value={item}>{item}</option>)}
                        </Select>
                    )}
                />

                <Table>
                    <THead>
                        <tr>
                            <TH>Waktu</TH>
                            <TH>Pengguna</TH>
                            <TH>Aksi</TH>
                            <TH>Modul</TH>
                            <TH>Deskripsi</TH>
                            <TH>IP</TH>
                        </tr>
                    </THead>

                    <TBody>
                        {rows.map(log => {
                            const user = log.userId ? userById.get(log.userId) : undefined

                            return (
                                <TR key={log.id}>
                                    <TD className='text-xs whitespace-nowrap text-muted-foreground tabular-nums'>{formatDateTime(log.createdAt)}</TD>
                                    <TD className='whitespace-nowrap'>
                                        <span className='font-semibold text-foreground'>{user?.name ?? '—'}</span>
                                        {user && <span className='ml-1.5 text-xs text-muted-foreground'>{user.username}</span>}
                                    </TD>
                                    <TD><Badge tone='neutral'>{log.action}</Badge></TD>
                                    <TD className='whitespace-nowrap'>{log.module}</TD>
                                    <TD className='min-w-64 text-foreground'>{log.description}</TD>
                                    <TD className='font-mono text-xs whitespace-nowrap text-muted-foreground'>{log.ip}</TD>
                                </TR>
                            )
                        })}

                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className='px-5 py-12 text-center text-sm text-muted-foreground'>
                                    Belum ada log aktivitas.
                                </td>
                            </tr>
                        )}
                    </TBody>
                </Table>
            </Card>
        </>
    )
}

export default ActivityLogsPage
